import React from "react";
import {
  X,
  ArrowRight,
  CheckCircle2,
  Zap,
  LayoutTemplate,
  Sliders,
} from "lucide-react";

export default function WeekModal({ open, onClose, data, mode }) {
  if (!open || !data) return null;

  // Safe defaults if data is missing specific fields
  const title = data.title || "Week Detail";
  const subtitle = data.subtitle || "System Adjustment";
  const why = data.why || "Aligning volume with recovery capacity.";
  const stays = data.stays || [];
  const changes = data.changes || [];
  const checklist = data.checklist || [];
  const coachLine = data.coachLine || "Trust the process.";

  // Dynamic Button Label based on Mode
  const btnLabel = mode === "ACCEPT" ? "Confirm & Unlock Plan" : "Got it";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        backgroundColor: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)",
        animation: "fadeIn 0.2s ease-out",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "520px",
          background: "#0b0c10",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "24px",
          boxShadow: "0 40px 80px -12px rgba(0, 0, 0, 0.6)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh", // Prevent overflow on small screens
          animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "32px 32px 24px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "24px",
              right: "24px",
              background: "rgba(255,255,255,0.05)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>

          <div
            style={{
              fontSize: "11px",
              fontWeight: 800,
              color: "var(--brand)",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "12px",
            }}
          >
            SYSTEM UPDATE
          </div>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: 800,
              margin: "0 0 8px 0",
              color: "#fff",
              lineHeight: "1.1",
            }}
          >
            {title}
          </h2>
          <div
            style={{
              fontSize: "15px",
              color: "var(--muted)",
              lineHeight: "1.5",
            }}
          >
            {subtitle}
          </div>
        </div>

        {/* SCROLLABLE BODY */}
        <div style={{ padding: "32px", overflowY: "auto" }}>
          {/* 1. THE LOGIC */}
          <div style={{ marginBottom: "32px" }}>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 800,
                color: "#fff",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "8px",
              }}
            >
              The Logic
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                lineHeight: "1.6",
                color: "var(--muted)",
              }}
            >
              {why}
            </p>
          </div>

          {/* 2. THE SHIFT (Stays vs Changes) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginBottom: "32px",
            }}
          >
            {/* STAYS */}
            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                }}
              >
                <LayoutTemplate size={14} /> Keeping
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {stays.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: "13px",
                      color: "#e2e8f0",
                      marginBottom: "8px",
                      lineHeight: "1.4",
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* CHANGES */}
            <div
              style={{
                background: "rgba(34,197,94,0.05)",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid rgba(34,197,94,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "var(--brand)",
                  textTransform: "uppercase",
                }}
              >
                <Sliders size={14} /> Changing
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {changes.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: "13px",
                      color: "#fff",
                      marginBottom: "8px",
                      lineHeight: "1.4",
                      fontWeight: "500",
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 3. CHECKLIST */}
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 800,
                color: "#fff",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "12px",
              }}
            >
              Execution Checklist
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {checklist.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontSize: "14px",
                    color: "var(--muted)",
                  }}
                >
                  <div
                    style={{
                      minWidth: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.1)",
                    }}
                  />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "20px 32px",
            textAlign: "center",
            background: "#0f172a",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: "var(--muted)",
              fontStyle: "italic",
              marginBottom: "16px",
            }}
          >
            "{coachLine}"
          </div>
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "12px",
              background:
                mode === "ACCEPT" ? "var(--brand)" : "rgba(255,255,255,0.1)",
              color: mode === "ACCEPT" ? "#022c22" : "#fff",
              fontWeight: 800,
              border: "none",
              fontSize: "14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {btnLabel} <ArrowRight size={16} />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}
